import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TabNavigation from '../../components/ui/TabNavigation';
import PrivacyToggle from '../../components/ui/PrivacyToggle';
import QuickActionFloat from '../../components/ui/QuickActionFloat';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import AccountSettings from './components/AccountSettings';
import PrivacySettings from './components/PrivacySettings';
import NotificationSettings from './components/NotificationSettings';
import AppSettings from './components/AppSettings';
import AuditLogs from './components/AuditLogs';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const tabs = [
    { id: 'account', label: 'Account', icon: 'User' },
    { id: 'privacy', label: 'Privacy', icon: 'Lock' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'app', label: 'App Settings', icon: 'Settings' },
    { id: 'audit', label: 'Audit Logs', icon: 'FileText' },
    { id: 'debug', label: 'Debug Console', icon: 'Terminal' }
  ];

  const handleSaveChanges = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setHasUnsavedChanges(false);
      alert('Settings saved successfully!');
    }, 1000);
  };

  const handleResetToDefault = () => {
    if (window.confirm('Are you sure you want to reset all settings to default? This action cannot be undone.')) {
      setHasUnsavedChanges(false);
      alert('Settings reset to default values.');
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(false);
    alert('Account deletion request submitted. You will receive a confirmation email.');
  };

  const handleSettingChange = () => {
    setHasUnsavedChanges(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <PrivacyToggle />
      <QuickActionFloat />

      <main className="pt-[60px] pb-[72px] lg:pt-[76px] lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-6 lg:mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
                  Profile Settings
                </h1>
                <p className="text-muted-foreground font-body">
                  Manage your account preferences, privacy controls, and app settings
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="hidden lg:flex"
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border shadow-sm">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex space-x-1 p-2 min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center space-x-2 px-4 py-2.5 rounded-md
                      transition-therapeutic font-medium text-sm whitespace-nowrap
                      ${activeTab === tab?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <Icon 
                      name={tab?.icon} 
                      size={18} 
                      color={activeTab === tab?.id ? 'var(--color-primary-foreground)' : 'currentColor'}
                    />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8">
              {activeTab === 'account' && <AccountSettings onChange={handleSettingChange} />}
              {activeTab === 'privacy' && <PrivacySettings onChange={handleSettingChange} />}
              {activeTab === 'notifications' && <NotificationSettings onChange={handleSettingChange} />}
              {activeTab === 'app' && <AppSettings onChange={handleSettingChange} onDeleteAccount={() => setShowDeleteConfirm(true)} />}
              {activeTab === 'audit' && <AuditLogs />}
              {activeTab === 'debug' && (
                <div className="space-y-4">
                  <div className="bg-muted/30 border border-border rounded-lg p-6">
                    <div className="flex items-start gap-4">
                      <Icon name="Terminal" size={24} color="var(--color-primary)" />
                      <div className="flex-1">
                        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                          Admin Debug Console
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Access comprehensive troubleshooting tools for profile data persistence, real-time database monitoring, and field-by-field save status verification.
                        </p>
                        <Button
                          onClick={() => navigate('/admin-debug-console')}
                          className="flex items-center gap-2"
                        >
                          <Icon name="ExternalLink" size={16} />
                          Open Debug Console
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-border p-4 md:p-6 bg-muted/30">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  {hasUnsavedChanges && (
                    <>
                      <Icon name="AlertCircle" size={16} color="var(--color-warning)" />
                      <span>You have unsaved changes</span>
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={handleResetToDefault}
                    className="flex-1 sm:flex-none"
                  >
                    Reset to Default
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    loading={isSaving}
                    disabled={!hasUnsavedChanges}
                    className="flex-1 sm:flex-none"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-modal bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border shadow-lg max-w-md w-full p-6">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-1">
                  Delete Account
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. All your data, including mood history, encounters, and partner connections will be permanently deleted.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAccount}
              >
                Delete Account
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;